'use client';
// Édition des REGISTRES repris d'Excel — les gestes communs (lots 1-2 du
// chantier éditabilité, 10/08/2026) : créer, corriger, archiver avec motif.
//
// Un seul formulaire piloté par configuration et une seule modale d'archivage,
// partagés entre Stock, Matériaux reconstitués et programmes CQE : trois
// variantes auraient trois validations à tenir alignées, et c'est toujours la
// troisième qu'on oublie (le motif est le même que RATTACHES dans
// backend/src/api/ged/controllers/emplacement.ts).
//
// Deux règles, décidées par l'utilisatrice le 10/08/2026 :
//  · JAMAIS de suppression — l'archivage motivé, réversible, seulement ;
//  · pied de modale : « Annuler » PUIS l'action, alignés à droite.
import React, { useEffect, useRef, useState } from 'react';
import { I, Modale, inputStyle, useBusy, Pastille } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { ChoixCorrespondant } from '@/components/ChoixCorrespondant';
import { useGed } from '@/lib/store';

export type ChampDef = {
  k: string; label: string; requis?: boolean;
  /** `date` : sélecteur du navigateur, valeur `aaaa-mm-jj` (cf. `ChampDate`). */
  type?: 'long' | 'options' | 'date' | 'contact'; options?: readonly (readonly [string, string])[];
  /** `contact` : une fiche de l'ANNUAIRE (`ORG-…`, `PER-…`), choisie par le sélecteur commun — limitée à ces familles. */
  familles?: readonly string[];
  /** Suggestions de saisie (datalist) : la valeur reste libre — pour un code
   *  d'équipement, proposer le parc sans interdire un code que l'écran ignore. */
  suggestions?: readonly string[];
  /** Largeur pleine dans la grille (contenu, remarque). */
  large?: boolean;
  /** Identité figée : affichée mais verrouillée en correction. */
  fige?: boolean;
  /** Règles affichées SOUS L'ÉTIQUETTE, avant la saisie.
   *
   *  Demande de la RQ du 18/08/2026 pour les codes de paramètre et d'examen :
   *  un message d'erreur arrive APRÈS la faute, quand on a déjà choisi son code
   *  et qu'on y tient ; les règles écrites devant le champ orientent le choix,
   *  et l'erreur ne sert plus qu'aux cas limites. */
  regles?: readonly string[];
  /** Contrôle de la saisie, dont le message s'affiche sous le champ. */
  verifier?: (v: string) => { valide: boolean; motif: string };
  /** L'UNITÉ, affichée contre le bord droit du champ.
   *
   *  Elle n'est pas rangée entre parenthèses dans l'étiquette, où elle se lit
   *  AVANT la saisie et s'oublie pendant : « Stabilité congelé (jours) » se
   *  remplit « 30 j » une fois sur trois, et « 30 j » n'est pas un nombre pour
   *  le moteur, qui refuse la ligne. Contre le champ, elle est sous les yeux au
   *  moment où l'on tape. Elle reste DANS l'étiquette au sens accessible : le
   *  texte est à l'intérieur du `<label>` qui entoure la saisie. */
  unite?: string;
  /** Ce qu'on attend, montré dans le champ vide — « 3000 », « aaaa-mm-jj ».
   *  Un exemple n'est pas une valeur : il disparaît dès la première frappe et
   *  ne s'enregistre jamais. */
  exemple?: string;
  /** Une phrase d'aide SOUS le champ — la règle que le moteur appliquera
   *  (bornes d'un nombre, origine d'une date). `regles` reste pour ce qui
   *  oriente un CHOIX avant la saisie ; `aide` dit ce qui sera accepté. */
  aide?: string;
  /**
   * CE CHAMP N'A DE SENS QUE SI… — masquage piloté par une AUTRE saisie du
   * même formulaire (06/10/2026 : « date de reconstitution » sur un réactif
   * prêt à l'emploi).
   *
   * ⚠️ MASQUÉ N'EST PAS EFFACÉ. La valeur reste dans l'état et part dans
   * l'enregistrement telle quelle : le formulaire renvoie ce qu'il PORTE, pas
   * ce qu'il montre. Vider en même temps que masquer ferait disparaître une
   * donnée par un geste qui ne la visait pas — c'est le mécanisme déjà décrit
   * pour les valeurs hors liste et pour les dates illisibles.
   *
   * ⚠️ Un champ masqué n'est PAS réclamé, même `requis` : un bandeau rouge sur
   * un champ qu'on ne voit pas est un formulaire qu'on ne peut plus envoyer.
   */
  visibleSi?: (v: Record<string, string>) => boolean;
};

/** `aaaa-mm-jj` — la SEULE forme que le moteur lit comme une date (`DATE`,
 *  backend/src/materiaux-echeance.ts). Écrite autrement, une date est gardée
 *  telle quelle en base, mais aucune échéance ne se calcule dessus. */
const JOUR_ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * SAISIE D'UNE DATE — le sélecteur du navigateur, et une sortie de secours.
 *
 * ⚠️ UNE DATE ILLISIBLE RESTE EN SAISIE LIBRE, ET LE DIT.
 *
 * Ces registres viennent d'une reprise Excel et portent des dates écrites à la
 * main : « avant 2022 », « 03/2026 ». Un `input type="date"` ne sait pas les
 * afficher — il se montrerait VIDE alors que la ligne porte la valeur, et
 * l'enregistrement, qui renvoie ce que le formulaire affiche, l'aurait
 * EFFACÉE. Une donnée perdue par un geste qui ne la visait pas : c'est le
 * mécanisme décrit plus haut pour les valeurs hors liste, un cran plus loin.
 *
 * Le passage au sélecteur est donc un CHOIX, et il vide le champ ; et la
 * saisie libre reste atteignable pour une date imprécise, dite comme telle.
 */
function ChampDate({ def, valeur, onChange, verrouille }: {
  def: ChampDef; valeur: string; onChange: (v: string) => void; verrouille?: boolean;
}) {
  const brut = valeur.trim();
  const lisible = brut === '' || JOUR_ISO.test(brut);
  const [libre, setLibre] = useState(!lisible);

  if (libre) {
    return (
      <>
        <input value={valeur} disabled={verrouille} onChange={(e) => onChange(e.target.value)}
          placeholder={def.exemple || 'aaaa-mm-jj'}
          style={{ ...inputStyle, fontWeight: 400, opacity: verrouille ? 0.65 : 1 }} />
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontWeight: 400, fontSize: 12.5, color: 'var(--amber-700, #92400e)' }}>
          {brut && !lisible
            ? <>« {brut} » n’est pas une date que l’outil sait lire : elle est gardée telle quelle, mais aucune échéance ne s’en déduit.</>
            : <>Saisie libre : ce qui sera écrit ici ne compte pas comme une date.</>}
          {!verrouille && (
            <Bouton variante="discret" taille="sm" onClick={() => { onChange(''); setLibre(false); }}>
              Choisir une date
            </Bouton>
          )}
        </span>
      </>
    );
  }
  return (
    <>
      <input type="date" value={brut} disabled={verrouille} onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, fontWeight: 400, opacity: verrouille ? 0.65 : 1 }} />
      {!verrouille && brut === '' && (
        <Bouton variante="discret" taille="sm" onClick={() => setLibre(true)}
          title="Pour une date imprécise — « avant 2022 », « 03/2026 ». Elle est conservée telle quelle, et aucune échéance ne s’en déduit."
          style={{ justifySelf: 'start' }}>
          Date imprécise
        </Bouton>
      )}
    </>
  );
}

/** L'unité contre le bord droit du champ (cf. `ChampDef.unite`). */
function AvecUnite({ unite, children }: { unite?: string; children: React.ReactNode }) {
  if (!unite) return <>{children}</>;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {children}
      <span style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{unite}</span>
    </span>
  );
}

export function Champ({ def, valeur, onChange, verrouille }: {
  def: ChampDef; valeur: string; onChange: (v: string) => void; verrouille?: boolean;
}) {
  return (
    <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)', gridColumn: def.large || def.type === 'long' ? '1 / -1' : undefined }}>
      <span>
        {def.label}{def.requis && <span style={{ color: 'var(--red-600)' }}> *</span>}
        {verrouille && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }} title="Identité de la ligne : d’autres registres la désignent par elle — elle ne se renumérote pas."> · figé</span>}
      </span>
      {def.regles && def.regles.length > 0 && (
        <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12.5, fontWeight: 400, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {def.regles.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      {def.type === 'contact' ? (
        <ChoixCorrespondant id={`champ-${def.k}`} valeur={valeur} onChange={onChange} actif={!verrouille} familles={def.familles} vide="— chercher dans l’annuaire —"
          aide={def.exemple || 'Choisi dans l’annuaire : son nom y est tenu à jour.'} />
      ) : def.type === 'options' ? (
        <select value={valeur} disabled={verrouille} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, fontWeight: 400 }}>
          {/* L'option vide n'existe QUE si la valeur est vide : sans elle, le
              navigateur affiche la première option de la liste alors que l'état
              vaut '' — l'écran affirmerait une valeur que la ligne n'a pas, et
              c'est précisément ce qu'on vient d'empêcher plus bas. Elle
              disparaît dès qu'un choix est fait, pour ne pas offrir « vider »
              comme une option ordinaire. */}
          {valeur.trim() === '' && <option value="">— non renseigné —</option>}
          {(def.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          {/* VALEUR HORS LISTE — conservée, jamais remplacée en silence.
              Ces registres viennent d'une reprise Excel : ils portent des mots
              du classeur qu'aucune liste ne prévoyait (« asma/naim »,
              « LAM B HSSAN »). Sans cette option, le `select` n'aurait rien à
              afficher pour cette valeur, se rabattrait sur la première de la
              liste, et le simple fait D'OUVRIR le formulaire aurait réécrit la
              ligne à l'enregistrement — une donnée changée par un geste qui ne
              la visait pas. Elle est montrée telle quelle et marquée, pour que
              son remplacement soit un CHOIX. */}
          {valeur.trim() !== '' && !(def.options || []).some(([v]) => v === valeur) && (
            <option value={valeur}>{valeur} (hors liste)</option>
          )}
        </select>
      ) : def.type === 'long' ? (
        <textarea value={valeur} disabled={verrouille} onChange={(e) => onChange(e.target.value)} rows={3}
          placeholder={def.exemple}
          style={{ ...inputStyle, height: 'auto', padding: '9px 12px', fontWeight: 400, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
      ) : def.type === 'date' ? (
        <ChampDate def={def} valeur={valeur} onChange={onChange} verrouille={verrouille} />
      ) : (
        <>
          <AvecUnite unite={def.unite}>
            <input value={valeur} disabled={verrouille} onChange={(e) => onChange(e.target.value)}
              placeholder={def.exemple}
              list={def.suggestions?.length ? `dl-${def.k}` : undefined}
              style={{ ...inputStyle, fontWeight: 400, opacity: verrouille ? 0.65 : 1 }} />
          </AvecUnite>
          {def.suggestions?.length ? (
            <datalist id={`dl-${def.k}`}>
              {def.suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
          ) : null}
        </>
      )}
      {def.aide && (
        <span style={{ fontWeight: 400, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{def.aide}</span>
      )}
      {(() => {
        // Le verdict s'affiche SOUS le champ, et seulement quand quelque chose
        // a été saisi : signaler « obligatoire » sur un champ vierge que
        // l'utilisateur n'a pas encore atteint le fait passer pour fautif.
        if (!def.verifier || !valeur.trim()) return null;
        const v = def.verifier(valeur);
        return v.valide ? null : (
          <span role="alert" style={{ fontSize: 12.5, fontWeight: 400, color: 'var(--red-700, #9B1C1C)' }}>{v.motif}</span>
        );
      })()}
    </label>
  );
}

/** Création / correction d'une ligne. Les valeurs restent les MOTS DU LABO :
 *  aucun champ n'est normalisé à la saisie — « Avant 2022 » et « NON FAIT »
 *  ont le droit d'entrer, comme dans le classeur. */
export function FormulaireLigne({
  chemin, titreCreation, champs, ligne, complementPayload, onClose, onDone, suffixe, titreCorrection,
}: {
  /** Chemin moteur SANS l'id : `ged/stock/lieux`, `ged/materiaux/calibrants`… */
  chemin: string;
  /** Segment d'action APRÈS l'id (`/corriger`) quand la route de correction
   *  n'est pas la route de création. */
  suffixe?: string;
  titreCorrection?: string;
  titreCreation: string; champs: readonly ChampDef[];
  /** `null` = création. */
  ligne: Record<string, unknown> | null;
  /** Champs calculés à l'enregistrement (le `code` d'un lieu, dérivé du triplet). */
  complementPayload?: (v: Record<string, string>) => Record<string, string>;
  onClose: () => void; onDone: () => void;
}) {
  const { smq } = useGed();
  const { run, estOccupe } = useBusy();
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(
    /* ⚠️ EN CORRECTION, AUCUNE VALEUR PAR DÉFAUT (27/08/2026).
       La première option d'une liste n'est un bon choix qu'À LA CRÉATION.
       Sur une ligne existante, elle affirme une valeur que la ligne n'a pas :
       si la réponse du serveur omet le champ — parce qu'il s'écrit mais ne se
       relit pas — le formulaire le reconstruit, et l'enregistrement l'écrit.
       Le paramètre `GLY` a failli devenir « quantitatif » parce qu'on venait
       corriger son unité. C'est le mécanisme décrit 60 lignes plus haut pour
       les valeurs hors liste, un cran plus tôt : là, la valeur existait et
       n'était pas dans la liste ; ici, elle n'arrive pas du tout. */
    champs.map((c) => [c.k, String((ligne?.[c.k] as string | undefined)
      ?? (!ligne && c.type === 'options' ? c.options?.[0]?.[0] ?? '' : ''))]),
  ));
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026), sous la forme
     exacte du corps et joint sous `base` en correction : le moteur n'écrit que
     les champs changés depuis cette lecture, et refuse (409) un champ qu'un
     autre a changé entre-temps — au lieu de ramener sa valeur périmée. */
  const [base] = useState<Record<string, string>>(() => ({ ...v, ...(complementPayload ? complementPayload(v) : {}) }));
  const [erreur, setErreur] = useState('');
  /* Les champs RENDUS — `visibleSi` les retire de l'affichage ET du contrôle
     d'obligation, jamais de l'état ni du corps envoyé (voir `ChampDef`). */
  const champsVus = champs.filter((c) => !c.visibleSi || c.visibleSi(v));
  const manquant = champsVus.find((c) => c.requis && !v[c.k].trim());

  const enregistrer = () => run('save', async () => {
    if (manquant) { setErreur(`« ${manquant.label} » est obligatoire.`); return; }
    const payload: Record<string, unknown> = { ...v, ...(complementPayload ? complementPayload(v) : {}) };
    if (ligne) payload.base = base;
    const id = ligne ? `/${String(ligne.documentId)}${suffixe || ''}` : '';
    const ok = await smq(`${chemin}${id}`, payload,
      ligne ? 'Ligne corrigée — la différence est au journal.' : 'Ligne ajoutée au registre.');
    if (ok) { onDone(); onClose(); }
  });

  return (
    <Modale titre={ligne ? (titreCorrection || 'Corriger la ligne') : titreCreation} onClose={onClose}
      sousTitre={ligne ? 'La correction est tracée : la différence champ par champ s’écrit au journal du registre.' : undefined}
      pied={(
        <PiedActions
          annuler={{ onClick: onClose }}
          principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe(), disabled: estOccupe(), onClick: enregistrer }}
        />
      )}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {champsVus.map((c) => (
          <Champ key={c.k} def={c} valeur={v[c.k]} verrouille={!!ligne && c.fige}
            onChange={(val) => { setErreur(''); setV((s) => ({ ...s, [c.k]: val })); }} />
        ))}
      </div>
      {erreur && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)' }}>{erreur}</div>}
    </Modale>
  );
}

/** Archivage motivé (jamais de suppression), et rétablissement. */
export function ArchiverModale({ chemin, ligne, refLigne, onClose, onDone }: {
  chemin: string;
  ligne: { documentId: string; archive?: boolean | null; archiveMotif?: string | null };
  refLigne: string;
  onClose: () => void; onDone: () => void;
}) {
  const { smq } = useGed();
  const { run, estOccupe } = useBusy();
  const retablir = !!ligne.archive;
  const [motif, setMotif] = useState('');

  const envoyer = () => run('arch', async () => {
    const ok = await smq(`${chemin}/${ligne.documentId}/archiver`,
      { archive: !retablir, motif },
      retablir ? 'Ligne rétablie dans le registre courant.' : 'Ligne archivée — elle reste citable, elle n’est pas effacée.');
    if (ok) { onDone(); onClose(); }
  });

  return (
    <Modale titre={retablir ? 'Rétablir la ligne' : 'Archiver la ligne'} onClose={onClose}
      sousTitre={refLigne}
      pied={(
        <PiedActions
          annuler={{ onClick: onClose }}
          principale={{
            libelle: retablir ? 'Rétablir' : 'Archiver', libelleChargement: 'En cours…',
            chargement: estOccupe(), disabled: estOccupe() || (!retablir && !motif.trim()), onClick: envoyer,
          }}
        />
      )}>
      {retablir ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-body)' }}>
          La ligne revient dans les vues courantes. Son archivage précédent
          {ligne.archiveMotif ? <> (« {ligne.archiveMotif} »)</> : null} reste au journal.
        </p>
      ) : (
        <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>
          <span>Motif d’archivage <span style={{ color: 'var(--red-600)' }}>*</span></span>
          {/* 255 : la place du motif en base, dans tous les registres de cette
              fenêtre — au-delà le moteur refuse (`MOTIF_ARCHIVE_MAX`, stock.ts). */}
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} autoFocus maxLength={255}
            placeholder="Pourquoi cette ligne sort du registre courant — c’est ce qu’on relira dans deux ans."
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', fontWeight: 400, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
          <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12.5 }}>
            Rien n’est effacé : la ligne reste citable par une non-conformité ou un audit, et revient par « Rétablir ».
            {' '}{motif.length}/255 caractères.
          </span>
        </label>
      )}
    </Modale>
  );
}

/**
 * Colonne d'actions de ligne — RQ seulement.
 *
 * `onArchiver` et `onSupprimer` sont tous deux FACULTATIFS : chaque registre
 * dit lequel des deux gestes a un sens chez lui, et n'affiche que celui-là.
 *
 * Archiver garde la ligne citable par une NC ou un audit ; supprimer ne garde
 * que la trace au journal. La RQ a tranché le 26/08/2026 pour le contrôle
 * externe : « je ne veux rien archiver, je veux supprimer » — ce qu'on y retire
 * est de la reprise fausse et de l'import d'essai, pas de la campagne vécue.
 *
 * ⚠️ N'affichez pas les deux sur le même registre sans raison : deux boutons
 * voisins dont l'un est réversible et l'autre non, c'est une erreur qui attend
 * son jour.
 */
export function ActionsLigne({ onCorriger, onArchiver, archive, onSupprimer, libelleSuppression }: {
  onCorriger: () => void; onArchiver?: () => void; archive?: boolean;
  onSupprimer?: () => void; libelleSuppression?: string;
}) {
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <Bouton taille="sm" onClick={onCorriger} title="Corriger la ligne" aria-label="Corriger la ligne">
        <I n="pen" s={13} />
      </Bouton>
      {onArchiver && (
        /* Archiver et rétablir sont RÉVERSIBLES : contour, jamais plein ni danger (UI-CONVENTIONS §4). */
        <Bouton taille="sm" onClick={onArchiver} title={archive ? 'Rétablir la ligne' : 'Archiver la ligne'}
          aria-label={archive ? 'Rétablir la ligne' : 'Archiver la ligne'}>
          <I n={archive ? 'refresh' : 'archive'} s={13} />
        </Bouton>
      )}
      {onSupprimer && (
        /* Suppression IRRÉVERSIBLE : danger. La confirmation relève de l'appelant (`onSupprimer`) :
           il doit passer par la confirmation de l'application, jamais `window.confirm`. */
        <Bouton variante="danger" taille="sm" onClick={onSupprimer}
          title={libelleSuppression || 'Supprimer définitivement la ligne'}
          aria-label={libelleSuppression || 'Supprimer définitivement la ligne'}>
          <I n="trash" s={13} />
        </Bouton>
      )}
    </span>
  );
}

/** Pastille discrète sur une ligne archivée, quand le filtre les montre. */
export const BadgeArchive = ({ motif }: { motif: string | null }) => (
  /* L9 : sur la Pastille du socle (É-12). */
  <Pastille ton="muted" compacte title={motif || undefined}><I n="archive" s={11} /> archivée</Pastille>
);

/** Ouvre la création quand l'en-tête de l'écran le demande (pattern « action
 *  primaire en haut à droite » : le bouton vit dans l'en-tête, le formulaire
 *  dans l'onglet). */
export function useDemandeCreation(signal: number, ouvrir: () => void) {
  const vu = useRef(signal);
  useEffect(() => {
    if (signal !== vu.current) { vu.current = signal; ouvrir(); }
  }, [signal, ouvrir]);
}
